import { create } from 'zustand';
import { X, AlertCircle } from 'lucide-react';
import { useEffect } from 'react';

// Popup display time in milliseconds (10 seconds)
const POPUP_DISPLAY_TIME = 10000;

// Interface for the error state
interface ErrorState {
  isOpen: boolean;
  message: string;
  setOpen: (open: boolean, message?: string) => void;
  setError: (message: string) => void;
  clearError: () => void;
}

// Create zustand store for managing error state
export const useErrorPopup = create<ErrorState>((set) => ({
  isOpen: false,
  message: '',
  setOpen: (open, message) => set({ isOpen: open, ...(message && { message }) }),
  setError: (message) => set({ isOpen: true, message }),
  clearError: () => set({ isOpen: false, message: '' }),
}));

// Main component
export default function ErrorPopup() {
  const { isOpen, message, clearError } = useErrorPopup();

  // Close on ESC key
  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        clearError();
      }
    };
    
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [clearError]);
  
  // Auto-close error after delay
  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => {
        clearError();
      }, POPUP_DISPLAY_TIME);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, clearError]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-x-hidden overflow-y-auto"
      onClick={(e) => {
        // Close when clicking on the overlay background
        if (e.target === e.currentTarget) {
          clearError();
        }
      }}
    >
      <div className="relative w-full max-w-md mx-auto">
        <div className="quantum-card p-6 border border-red-500/30 shadow-lg shadow-red-500/20">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-red-500 flex items-center">
              <AlertCircle className="w-6 h-6 mr-2" />
              Transaction Failed
            </h3>
            <button
              onClick={clearError}
              className="text-quantum-light/70 hover:text-quantum-light focus:outline-none"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="max-h-60 overflow-y-auto">
            <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
              <p className="text-quantum-light/90 break-words whitespace-pre-wrap">
                {message}
              </p>
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearError}
              className="px-4 py-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 